import React from 'react';
import { Card, Col, Input, Row, Space, Table, Typography } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { Search } from 'react-iconly';
import { useIntl } from 'react-intl';
import { RiAddLine } from 'react-icons/ri';
import { Filter } from 'iconsax-react';
import { Permissions } from '../../../../../const/permissions';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import basePagination from '../../../../../const/pagination';
import urlPageTemplates from '../../../../../urls/urlPageTemplates';
import urlPageTemplatesVisualizationsCreate from '../../../../../urls/urlPageTemplatesVisualizationsCreate';
import urlPageTemplatesVisualizationsEdit from '../../../../../urls/urlPageTemplatesVisualizationsEdit';
import useQueryParams from '../../../../../app/hooks/useQueryParams';
import useElementSize from '../../../../../app/hooks/useElementSize';
import useToggle from '../../../../../app/hooks/useToggle';
import resetFilter from '../../../../../lib/resetFilter';
import applyFilter from '../../../../../lib/applyFilter';
import makeTableLoadingObject from '../../../../../lib/makeTableLoadingObject';
import handleResponse from '../../../../../lib/handleResponse';
import { getTemplatesVisualizationsTableColumns } from './PageTemplatesVisualizations.const';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../layout/components/action-button';
import Sidebar from '../../../../../components/Sidebar';
import DocumentationTooltip from '../../../../../components/DocumentationTooltip/DocumentationTooltip';
import FormFilterTemplatesVisualizations from '../../../forms/FormFilterTemplatesVisualizations';
import { useGetTemplatesVisualizationsQuery, useDeleteTemplatesVisualizationMutation } from '../../../api/templatesVisualizationsApiSlice';



const { Title } = Typography;

const PageTemplatesVisualizations = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ elementRef, size ] = useElementSize();
  const [ isVisibleFilterSidebar, toggleFilterSidebar ] = useToggle();
  const [ searchQueryParams, setSearchParams ] = useSearchParams();

  const searchFields = [ 'title' ];
  const searchFilterFields = [ 'id', 'title', 'template', 'created_from', 'created_to', 'is_default' ];

  const {
    pagination,
    search,
    setSearchTerm,
    searchParams,
    initFilterValues,
    setInitFilterValues,
    handleChangeTableParams,
    resetIsManuallyTriggered,
    setResetManuallyTriggered,
  } = useQueryParams({ searchFields, searchFilterFields });

  const searchParamsString = searchParams.toString();

  const {
    data = { data: [], pagination: {} },
    isFetching,
    error: formErrors = {},
  } = useGetTemplatesVisualizationsQuery({ queryParams: searchParamsString }, { skip: !searchParamsString });

  const [ deleteTemplatesVisualization, { isLoading: isDeletingTemplatesVisualization } ] = useDeleteTemplatesVisualizationMutation();

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setResetManuallyTriggered(false);
  };

  const handleDelete = (visualizationId) => {
    deleteTemplatesVisualization(visualizationId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleEdit = (visualizationId) => {
    navigate(urlPageTemplatesVisualizationsEdit({ visualizationId }));
  };

  const isLoading = isFetching || isDeletingTemplatesVisualization;

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageTemplates()}>
              <IntlMessages id='templates-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={
            <div>
              <IntlMessages id='templates-visualizations-breadcrumbs' />
              <DocumentationTooltip link='https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/1398767635/Visualization+data' />
            </div>
          }
        />

        <Col md={12} span={24}>
          <Row
            gutter={[ 16, 16 ]}
            justify="end"
            align="middle"
          >
            <Col md={12} span={24}>
              <Input
                data-testid="search-input"
                allowClear={!isEmpty(search)}
                placeholder={intl.formatMessage({ id: 'ui-general-search-by-title' })}
                prefix={<Search set="curved" size={16} className="hp-text-color-black-80" />}
                value={search}
                onChange={handleSearch}
              />
            </Col>
            <Col>
              <Space>
                <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.CREATE ]}>
                  <ActionButton
                    onClick={() => navigate(urlPageTemplatesVisualizationsCreate())}
                    icon={<RiAddLine className="btn-icon-mr-1" />}
                    title={<IntlMessages id='ui-general-create' />}
                  />
                </PrivateComponent>
                <ActionButton
                  title=""
                  isActive={!isEmpty(initFilterValues)}
                  icon={<Filter size={18} data-testid="filter-action-icon" />}
                  onClick={toggleFilterSidebar}
                />
              </Space>
            </Col>
          </Row>
        </Col>

        <Sidebar
          key='templates-visualizations-filter'
          title={<Title level={5}>{intl.formatMessage({ id: 'templates-visualizations-data-filter-title' })}</Title>}
          visible={isVisibleFilterSidebar}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormFilterTemplatesVisualizations
            initialValues={initFilterValues}
            formErrors={formErrors}
            isSubmitting={isFetching}
            onCancel={() => {
              setResetManuallyTriggered(true);

              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                resetIsManuallyTriggered,
                setSearchTerm,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values,
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                setSearchField: setSearchTerm,
                toggleFilterSidebar,
              });
            }}
          />
        </Sidebar>

      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <div ref={elementRef}>
              <Table
                sticky
                scroll={{ x: 860 }}
                loading={makeTableLoadingObject(isLoading)}
                rowKey="id"
                columns={getTemplatesVisualizationsTableColumns(handleEdit, handleDelete, size)}
                dataSource={data.data}
                onChange={handleChangeTableParams}
                pagination={{
                  ...basePagination,
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: data.pagination?.total,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};


export default PageTemplatesVisualizations;

